import { useState, useEffect } from "react";
import { DealbreakerQuestionInput } from "@/types/dealbreaker";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, X, ChevronUp, ChevronDown } from "lucide-react";

interface DealbreakerQuestionsSectionProps {
  questions: DealbreakerQuestionInput[];
  onChange: (questions: DealbreakerQuestionInput[]) => void;
}

export function DealbreakerQuestionsSection({
  questions,
  onChange,
}: DealbreakerQuestionsSectionProps) {
  const [localQuestions, setLocalQuestions] = useState<DealbreakerQuestionInput[]>(
    questions.length > 0 ? questions : []
  );

  // Sync with parent when localQuestions change
  useEffect(() => {
    onChange(localQuestions);
  }, [localQuestions, onChange]);

  const addQuestion = () => {
    if (localQuestions.length >= 5) return;

    const newQuestion: DealbreakerQuestionInput = {
      question_text: "",
      question_order: localQuestions.length + 1,
    };

    setLocalQuestions([...localQuestions, newQuestion]);
  };

  const removeQuestion = (index: number) => {
    const updated = localQuestions.filter((_, i) => i !== index);
    // Reorder remaining questions
    const reordered = updated.map((q, i) => ({
      ...q,
      question_order: i + 1,
    }));
    setLocalQuestions(reordered);
  };

  const updateQuestionText = (index: number, text: string) => {
    const updated = [...localQuestions];
    updated[index] = { ...updated[index], question_text: text };
    setLocalQuestions(updated);
  };

  const moveQuestion = (index: number, direction: "up" | "down") => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === localQuestions.length - 1)
    ) {
      return;
    }

    const newIndex = direction === "up" ? index - 1 : index + 1;
    const updated = [...localQuestions];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];

    // Update question_order after swap
    const reordered = updated.map((q, i) => ({
      ...q,
      question_order: i + 1,
    }));

    setLocalQuestions(reordered);
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Dealbreaker Questions (Optional)</CardTitle>
        <CardDescription>
          Create up to 5 questions that potential matches must answer before accepting a match with you.
          These help you learn important information upfront. Examples: "Are you willing to relocate?",
          "What are your career goals?", "How do you practice your faith daily?"
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {localQuestions.map((question, index) => (
          <div key={index} className="space-y-2 p-4 border rounded-lg bg-muted/30">
            <div className="flex items-center justify-between">
              <Label htmlFor={`question-${index}`}>
                Question {index + 1}
              </Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => moveQuestion(index, "up")}
                  disabled={index === 0}
                  title="Move up"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => moveQuestion(index, "down")}
                  disabled={index === localQuestions.length - 1}
                  title="Move down"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeQuestion(index)}
                  className="text-destructive hover:text-destructive"
                  title="Remove question"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-1">
              <Textarea
                id={`question-${index}`}
                placeholder="Enter your dealbreaker question..."
                value={question.question_text}
                onChange={(e) => updateQuestionText(index, e.target.value)}
                maxLength={200}
                rows={2}
                className="resize-none"
              />
              <div className="text-xs text-muted-foreground text-right">
                {question.question_text.length}/200 characters
              </div>
            </div>
          </div>
        ))}

        {localQuestions.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No dealbreaker questions yet. Click "Add Question" to create one.
          </div>
        )}

        <Button
          type="button"
          variant="outline"
          onClick={addQuestion}
          disabled={localQuestions.length >= 5}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Question {localQuestions.length > 0 && `(${localQuestions.length}/5)`}
        </Button>

        {localQuestions.length >= 5 && (
          <p className="text-sm text-muted-foreground text-center">
            Maximum of 5 questions reached
          </p>
        )}
      </CardContent>
    </Card>
  );
}
